// frontend/src/components/client/mobile/MobileChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Send, User, CheckCheck, Bot, Headphones, Trash2 } from 'lucide-react';
import { sendClientMessage, sendAssistantMessage } from '../../../services/clientApi';
import { useClientAuth } from '../../../contexts/ClientAuthContext';

// =============================================================================
// MARKDOWN RENDERER — bold, italic, puces, liens
// =============================================================================
const inlineFormat = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i} style={{ fontWeight: 700, color: '#fff' }}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i} style={{ fontStyle: 'italic' }}>{part.slice(1, -1)}</em>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} style={{ background: '#0f172a', color: '#c084fc', padding: '1px 5px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>{part.slice(1, -1)}</code>;
    return part;
  });
};

const renderMessage = (text) => {
  if (!text) return null;
  const trimmed = text.trim();
  if (trimmed.startsWith('{') && trimmed.includes('"tool"'))
    return <span style={{ color: '#facc15', fontSize: 12 }}>⚠️ Internal error. Please try again.</span>;

  const lines = text.split('\n');
  const elements = [];
  let key = 0;
  for (const line of lines) {
    if (!line.trim()) { elements.push(<div key={key++} style={{ height: 6 }} />); continue; }
    const bullet = line.match(/^[\s]*[-•*]\s+(.+)/);
    if (bullet) {
      elements.push(
        <div key={key++} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: '#a78bfa', flexShrink: 0, marginTop: 1 }}>•</span>
          <span>{inlineFormat(bullet[1])}</span>
        </div>
      );
      continue;
    }
    const numbered = line.match(/^[\s]*(\d+)[.)]\s+(.+)/);
    if (numbered) {
      elements.push(
        <div key={key++} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: '#a78bfa', flexShrink: 0 }}>{numbered[1]}.</span>
          <span>{inlineFormat(numbered[2])}</span>
        </div>
      );
      continue;
    }
    elements.push(<div key={key++} style={{ lineHeight: 1.55 }}>{inlineFormat(line)}</div>);
  }
  return <>{elements}</>;
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
const MobileChat = () => {
  const { client } = useClientAuth();
  const storageKey = `assistant_chat_mobile_${client?.id || 'unknown'}`;

  const [mode, setMode]                   = useState(() => localStorage.getItem('mobile_chat_mode') || 'support');
  const [messages, setMessages]           = useState([]);
  const [input, setInput]                 = useState('');
  const [loading, setLoading]             = useState(true);
  const [sending, setSending]             = useState(false);
  const [staffOnline, setStaffOnline]     = useState(false);
  const [aiThinking, setAiThinking]       = useState(false);

  const wsRef        = useRef(null);
  const bottomRef    = useRef(null);
  const inputRef     = useRef(null);
  const sessionIdRef = useRef(Date.now().toString());

  useEffect(() => { localStorage.setItem('mobile_chat_mode', mode); }, [mode]);

  useEffect(() => {
    if (mode === 'support') {
      connectWebSocket();
    } else {
      wsRef.current?.close();
      wsRef.current = null;
      setStaffOnline(false);
      loadAiHistory();
    }
    return () => wsRef.current?.close();
  }, [mode, client?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, aiThinking]);

  // ── WebSocket (mode support) ────────────────────────────────────────────────
  const connectWebSocket = () => {
    setLoading(true);
    const token = localStorage.getItem('client_access_token');
    if (!token) { setLoading(false); return; }
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:8001')
      .replace(/^https/, 'wss').replace(/^http/, 'ws').replace(/\/api\/?$/, '');
    wsRef.current = new WebSocket(`${base}/api/client/chat/ws?token=${token}`);
    wsRef.current.onopen    = () => { setStaffOnline(true); setLoading(false); };
    wsRef.current.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        setMessages(prev => [...prev, {
          id: d.id, message: d.message,
          sender_type: d.sender_type,
          created_at: d.created_at,
          is_read: d.is_read,
        }]);
      } catch {}
    };
    wsRef.current.onclose = (e) => {
      setStaffOnline(false);
      setLoading(false);
      if (e.code !== 1008 && e.code !== 1011) setTimeout(connectWebSocket, 3000);
    };
    wsRef.current.onerror = () => setLoading(false);
  };

  // ── Historique AI (localStorage) ────────────────────────────────────────────
  const loadAiHistory = () => {
    setLoading(true);
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) setMessages(JSON.parse(saved));
      else setMessages([WELCOME_MSG]);
    } catch { setMessages([WELCOME_MSG]); }
    setLoading(false);
  };

  const saveAiHistory = (msgs) => {
    localStorage.setItem(storageKey, JSON.stringify(msgs.slice(-50)));
  };

  const clearAiHistory = () => {
    localStorage.removeItem(storageKey);
    sessionIdRef.current = Date.now().toString();
    setMessages([WELCOME_MSG]);
  };

  // ── Envoi ───────────────────────────────────────────────────────────────────
  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    const tempId = `t_${Date.now()}`;
    const userMsg = {
      id: tempId, message: text,
      sender_type: 'client',
      created_at: new Date().toISOString(),
      is_read: false, is_temp: true,
    };
    setMessages(prev => { const n = [...prev, userMsg]; if (mode === 'assistant') saveAiHistory(n); return n; });
    if (!overrideText) { setInput(''); if (inputRef.current) inputRef.current.style.height = 'auto'; }
    setSending(true);

    try {
      if (mode === 'assistant') {
        setAiThinking(true);
        const res = await sendAssistantMessage(text, sessionIdRef.current);
        const aiMsg = {
          id: `ai_${Date.now()}`,
          message: res.data.reply,
          sender_type: 'assistant',
          created_at: new Date().toISOString(),
          is_read: true,
          quick_actions: res.data.quick_actions || [],
          actions_taken: res.data.actions_taken || [],
        };
        setMessages(prev => {
          const updated = prev.map(m => m.id === tempId ? { ...m, is_temp: false } : m);
          const final = [...updated, aiMsg];
          saveAiHistory(final);
          return final;
        });
        setAiThinking(false);
      } else {
        const res = await sendClientMessage(text);
        setMessages(prev => prev.map(m => m.id === tempId ? { ...res.data, is_temp: false } : m));
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      const errMsg = {
        id: `err_${Date.now()}`,
        message: mode === 'assistant'
          ? '⚠️ Assistant unavailable. Please try again.'
          : '❌ Send failed. Please retry.',
        sender_type: 'system',
        created_at: new Date().toISOString(),
      };
      setMessages(prev => { const n = [...prev, errMsg]; if (mode === 'assistant') saveAiHistory(n); return n; });
      setAiThinking(false);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts), diff = Date.now() - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min`;
    if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('en-US');
  };

  // ── Quick action button ─────────────────────────────────────────────────────
  const QuickActionButton = ({ label, action, type }) => {
    const isLink = type === 'link' || /^https?:\/\//i.test(action);
    return (
      <button
        onClick={() => isLink ? window.open(action, '_blank', 'noopener,noreferrer') : send(action)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 12px', marginRight: 6, marginBottom: 6,
          background: '#1e293b', border: '0.5px solid #334155',
          borderRadius: 20, fontSize: 12, color: '#cbd5e1',
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    );
  };

  // =============================================================================
  // RENDER
  // =============================================================================
  const isAI = mode === 'assistant';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 56px - env(safe-area-inset-top) - 72px)' }}>

      {/* ── Header ── */}
      <div style={{
        padding: '8px 12px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: '0.5px solid #1e293b', flexShrink: 0,
      }}>
        {/* Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 8, height: 8, borderRadius: 4,
            background: isAI ? '#a855f7' : (staffOnline ? '#22c55e' : '#475569'),
            boxShadow: isAI ? '0 0 6px #a855f7' : (staffOnline ? '0 0 6px #22c55e' : 'none'),
            animation: (isAI || staffOnline) ? 'pulse 2s infinite' : 'none',
          }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {isAI ? 'AI Assistant online' : (staffOnline ? 'Support online' : 'Support offline')}
          </span>
        </div>
        {/* Boutons droite */}
        <div style={{ display: 'flex', gap: 6 }}>
          {isAI && (
            <button
              onClick={clearAiHistory}
              style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', borderRadius: 8, display: 'flex' }}
              title="New conversation"
            >
              <Trash2 size={15} color="#475569" />
            </button>
          )}
          <button
            onClick={() => { setMessages([]); setMode(isAI ? 'support' : 'assistant'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '5px 10px', borderRadius: 14,
              background: isAI ? '#581c87' : '#1e3a5f',
              border: `0.5px solid ${isAI ? '#7c3aed' : '#1d4ed8'}`,
              color: isAI ? '#c084fc' : '#60a5fa', fontSize: 11, cursor: 'pointer',
            }}
          >
            {isAI ? <><Headphones size={12} /> Support</> : <><Bot size={12} /> Copilot AI</>}
          </button>
        </div>
      </div>

      {/* ── Messages ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg) } }
          @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.5 } }
          @keyframes bounce { 0%,100% { transform:translateY(0) } 50% { transform:translateY(-4px) } }
        `}</style>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
            <div style={{ width: 24, height: 24, border: '2px solid #334155', borderTopColor: isAI ? '#a855f7' : '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#475569', paddingTop: 40 }}>
            {isAI
              ? <Bot size={40} color="#7c3aed" />
              : <User size={40} color="#475569" />
            }
            <p style={{ fontSize: 14, textAlign: 'center', color: '#64748b', margin: 0 }}>
              {isAI ? 'Ask me anything!' : 'No messages yet'}
            </p>
            {isAI && (
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 }}>
                <QuickActionButton label="✈️ Flight 5C1 → KDFW" action="Calculate flight from 5C1 to KDFW" />
                <QuickActionButton label="⛽ Order fuel" action="Order 100 gallons for my plane tomorrow morning" />
                <QuickActionButton label="☁️ Weather KDFW" action="Weather at KDFW" />
              </div>
            )}
          </div>
        ) : (
          messages.map((msg) => {
            const isClient    = msg.sender_type === 'client';
            const isAssistant = msg.sender_type === 'assistant';
            const isSystem    = msg.sender_type === 'system';

            return (
              <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isClient ? 'flex-end' : 'flex-start' }}>
                {/* Avatar + nom */}
                {!isClient && !isSystem && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 11, flexShrink: 0,
                      background: isAssistant ? '#6d28d9' : '#1d4ed8',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isAssistant ? <Bot size={12} color="#fff" /> : <User size={12} color="#fff" />}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: isAssistant ? '#a78bfa' : '#60a5fa' }}>
                      {isAssistant ? 'Copilot Aero' : 'FBO Support'}
                    </span>
                    <span style={{ fontSize: 10, color: '#475569' }}>{formatTime(msg.created_at)}</span>
                  </div>
                )}

                {/* Bulle */}
                <div style={{
                  maxWidth: '82%',
                  background: isClient ? '#2563eb' : isSystem ? 'rgba(234,179,8,0.1)' : isAssistant ? 'rgba(109,40,217,0.2)' : '#1e293b',
                  border: isClient ? 'none' : isSystem ? '0.5px solid rgba(234,179,8,0.4)' : isAssistant ? '0.5px solid rgba(139,92,246,0.4)' : '0.5px solid #334155',
                  borderRadius: isClient ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  padding: '10px 14px',
                  color: isSystem ? '#fbbf24' : '#f1f5f9',
                  fontSize: 14, lineHeight: 1.5,
                }}>
                  {isClient || isSystem
                    ? <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.message}</p>
                    : renderMessage(msg.message)
                  }

                  {/* Badges actions */}
                  {msg.actions_taken?.length > 0 && (
                    <div style={{ marginTop: 8, paddingTop: 6, borderTop: '0.5px solid rgba(139,92,246,0.3)', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {msg.actions_taken.map((a, i) => (
                        <span key={i} style={{ fontSize: 11, color: '#c084fc', background: 'rgba(109,40,217,0.3)', padding: '2px 8px', borderRadius: 10 }}>
                          {a.type === 'calculate_flight_plan' && '✈️ Flight plan calculated'}
                          {a.type === 'create_calendar_event' && '📅 Event added'}
                          {a.type === 'create_fuel_request'   && '⛽ Fuel request created'}
                          {a.type === 'get_partners'          && '🔍 Partners found'}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Quick actions */}
                  {msg.quick_actions?.length > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '0.5px solid rgba(100,116,139,0.3)', display: 'flex', flexWrap: 'wrap' }}>
                      {msg.quick_actions.map((qa, i) => (
                        <QuickActionButton key={i} label={qa.label} action={qa.action} type={qa.type} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Timestamp client */}
                {isClient && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                    <span style={{ fontSize: 10, color: '#475569' }}>{formatTime(msg.created_at)}</span>
                    {!msg.is_temp && <CheckCheck size={12} color={msg.is_read ? '#3b82f6' : '#475569'} />}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Indicateur "thinking" */}
        {aiThinking && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ width: 22, height: 22, borderRadius: 11, background: '#6d28d9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Bot size={12} color="#fff" />
            </div>
            <div style={{ background: 'rgba(109,40,217,0.2)', border: '0.5px solid rgba(139,92,246,0.4)', borderRadius: '18px 18px 18px 4px', padding: '12px 16px', display: 'flex', gap: 5, alignItems: 'center' }}>
              {[0, 150, 300].map(delay => (
                <div key={delay} style={{ width: 7, height: 7, borderRadius: '50%', background: '#a78bfa', animation: `bounce 1s ${delay}ms infinite` }} />
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input bar ── */}
      <div style={{
        padding: '10px 12px',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
        borderTop: '0.5px solid #1e293b',
        display: 'flex', alignItems: 'flex-end', gap: 8,
        background: 'rgba(15,23,42,0.97)',
        flexShrink: 0,
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => {
            setInput(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
          }}
          onKeyPress={e => {
            if (e.key === 'Enter' && !e.shiftKey && window.innerWidth >= 640) {
              e.preventDefault(); send();
            }
          }}
          placeholder={isAI ? 'Ask Copilot Aero…' : 'Message…'}
          rows={1}
          style={{
            flex: 1, background: '#1e293b',
            border: `0.5px solid ${isAI ? '#4c1d95' : '#334155'}`,
            borderRadius: 20, padding: '10px 14px', color: '#f1f5f9',
            fontSize: 15, resize: 'none', outline: 'none',
            maxHeight: 100, overflowY: 'auto', lineHeight: 1.4,
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            transition: 'border-color 0.2s',
          }}
        />
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          style={{
            width: 40, height: 40, borderRadius: 20, flexShrink: 0,
            background: input.trim() ? (isAI ? '#7c3aed' : '#2563eb') : '#1e293b',
            border: 'none', cursor: input.trim() ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >
          {sending
            ? <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            : <Send size={17} color={input.trim() ? '#fff' : '#475569'} />
          }
        </button>
      </div>
    </div>
  );
};

// Message de bienvenue AI
const WELCOME_MSG = {
  id: 'welcome',
  message: "Hello! I'm Copilot Aero, your personal assistant.\n\nI can:\n• Calculate flight plans\n• Order fuel\n• Check weather\n• Schedule flights & maintenance\n\nWhat can I do for you today?",
  sender_type: 'assistant',
  created_at: new Date().toISOString(),
  is_read: true,
};

export default MobileChat;